import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Plus, ShieldCheck, Trash2 } from "Components/ui/icons";

import { getApiErrorMessage, shouldRetryRequest } from "api/apiError";
import { deleteRole, fetchRoles, RbacRole, roleKeys } from "api/rbacRoles";
import { fetchUsers } from "api/users";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  notify,
  Page,
  PageHeader,
} from "Components/ui";
import { useAuth } from "hooks/useAuth";
import RoleEditorSheet, { RoleEditorState } from "./RoleEditorSheet";
import { isFullAccessRole, summarizeAccess, toPermissionRows } from "./permissions";

interface RoleManagerProps {
  /** Rendered inside the Settings panel, which has its own title. */
  embedded?: boolean;
}

interface RoleRow extends RbacRole {
  people: number;
}

const MAX_AREAS = 4;

/** "Full access", or the areas the role touches ("Tenants · view", "Billing · 3 of 4"). */
const AccessSummary: React.FC<{ role: RbacRole }> = ({ role }) => {
  if (isFullAccessRole(role)) {
    return (
      <Badge tone="primary" dot>
        Full access
      </Badge>
    );
  }
  const areas = summarizeAccess(toPermissionRows(role.permissions));
  if (areas.length === 0) {
    return <span className="text-sm text-muted-foreground">No permissions yet</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {areas.slice(0, MAX_AREAS).map((area) => (
        <Badge key={area.resource} tone={area.scope === "all" ? "info" : "neutral"}>
          {area.label}
          {area.scope === "view"
            ? " · view"
            : area.scope === "partial"
            ? ` · ${area.granted} of ${area.total}`
            : ""}
        </Badge>
      ))}
      {areas.length > MAX_AREAS && <Badge>+{areas.length - MAX_AREAS} more</Badge>}
    </div>
  );
};

/** Roles and what each may do; editing happens in a side panel. */
const RoleManager: React.FC<RoleManagerProps> = ({ embedded = false }) => {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("roleManagement", "write");
  const canDelete = hasPermission("roleManagement", "delete");
  const canSeeUsers = hasPermission("userManagement", "read");
  const queryClient = useQueryClient();

  const roles = useQuery({
    queryKey: roleKeys.all,
    queryFn: fetchRoles,
    retry: shouldRetryRequest,
  });
  const users = useQuery({
    queryKey: ["rbac", "users"],
    queryFn: fetchUsers,
    retry: shouldRetryRequest,
    enabled: canSeeUsers,
  });

  const [editor, setEditor] = React.useState<RoleEditorState>(null);
  const [deleting, setDeleting] = React.useState<RoleRow | null>(null);

  const rows = React.useMemo<RoleRow[]>(() => {
    const counts = new Map<string, number>();
    (users.data || []).forEach((user) => {
      const id = user.role?._id;
      if (id) {
        counts.set(id, (counts.get(id) || 0) + 1);
      }
    });
    return (roles.data || [])
      .map((role) => ({ ...role, people: counts.get(role._id) || 0 }))
      .sort(
        (a, b) =>
          Number(isFullAccessRole(b)) - Number(isFullAccessRole(a)) || a.name.localeCompare(b.name)
      );
  }, [roles.data, users.data]);

  const open = React.useCallback(
    (role: RbacRole) =>
      setEditor(
        canWrite && !isFullAccessRole(role) ? { mode: "edit", role } : { mode: "view", role }
      ),
    [canWrite]
  );

  const columns = React.useMemo<ColumnDef<RoleRow>[]>(
    () => [
      {
        id: "role",
        header: "Role",
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 font-medium">
              {isFullAccessRole(row.original) && (
                <ShieldCheck aria-hidden="true" className="h-4 w-4 text-primary-text" />
              )}
              {row.original.name}
            </p>
            {row.original.description && (
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {row.original.description}
              </p>
            )}
          </div>
        ),
      },
      {
        id: "access",
        header: "Can access",
        cell: ({ row }) => <AccessSummary role={row.original} />,
      },
      ...(canSeeUsers
        ? [
            {
              id: "people",
              header: "People",
              meta: { align: "right" },
              cell: ({ row }) => <span className="tabular-nums">{row.original.people}</span>,
            } as ColumnDef<RoleRow>,
          ]
        : []),
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: "right" },
        cell: ({ row }) => {
          const role = row.original;
          const editable = canWrite && !isFullAccessRole(role);
          const deletable = canDelete && !isFullAccessRole(role);
          return (
            <div className="flex items-center justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={() => open(role)}>
                {editable ? "Edit" : "View"}
              </Button>
              {deletable && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      label={`More for ${role.name}`}
                      icon={<MoreHorizontal />}
                      size="sm"
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      tone="danger"
                      icon={<Trash2 />}
                      onSelect={() => setDeleting(role)}
                    >
                      Delete role
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        },
      },
    ],
    [canDelete, canSeeUsers, canWrite, open]
  );

  const newRoleButton = canWrite ? (
    <Button leftIcon={<Plus />} onClick={() => setEditor({ mode: "create" })}>
      New role
    </Button>
  ) : null;

  const table = roles.isError ? (
    <Card>
      <ErrorState
        title="Roles didn't load"
        description={getApiErrorMessage(roles.error)}
        action={
          <Button variant="secondary" onClick={() => roles.refetch()}>
            Try again
          </Button>
        }
      />
    </Card>
  ) : (
    <Card className="p-5">
      <DataTable
        columns={columns}
        data={rows}
        loading={roles.isLoading}
        getRowId={(row) => row._id}
        // No row click: the actions menu closes on pointer-up and its click would land on the row.
        enableSorting={false}
        caption="Roles"
        empty={
          <EmptyState
            compact
            icon={<ShieldCheck />}
            title="No roles yet"
            description="Create a role, then give it to people on the Users page."
            action={newRoleButton}
          />
        }
      />
    </Card>
  );

  const body = (
    <>
      {table}
      <RoleEditorSheet state={editor} onClose={() => setEditor(null)} />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(next) => (next ? undefined : setDeleting(null))}
        tone="danger"
        title={deleting ? `Delete ${deleting.name}?` : "Delete role?"}
        description={
          deleting && deleting.people > 0
            ? `${deleting.people} ${
                deleting.people === 1 ? "person has" : "people have"
              } this role. Give them another role on the Users page first; a role that's in use can't be deleted.`
            : "This can't be undone."
        }
        confirmLabel="Delete role"
        confirmText={deleting?.name}
        onConfirm={async () => {
          if (!deleting) {
            return;
          }
          try {
            await deleteRole(deleting._id);
            notify.success(`${deleting.name} deleted`);
            queryClient.invalidateQueries({ queryKey: roleKeys.all });
          } catch (err) {
            notify.error(err, { fallback: "The role wasn't deleted." });
            throw err;
          }
        }}
      />
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-4">
        {newRoleButton && <div className="flex justify-end">{newRoleButton}</div>}
        {body}
      </div>
    );
  }

  return (
    <Page>
      <PageHeader
        title="Roles"
        description="What each role may do in SAP. People get their role on the Users page."
        actions={newRoleButton}
      />
      {body}
    </Page>
  );
};

export default RoleManager;
